import createReducer from '../../../../../../utils/createReducer';
import { ACTION_PREFIX } from '../../../sportsbook-constants';
import type { RootState, ThunkAction } from '~/store/types';
import selectSportsbookState from '../../../sportsbook-state-selector';
import { subscribeToEvents, unsubscribeFromEvents } from '../../../api/sportsbook-api';
import {
	isMessageEventAdded,
	isMessageEventDelta,
	isMessageEventRemoved,
	isMessageInitialState,
	isMessageMarketAdded,
	isMessageMarketDelta,
	isMessageMarketRemoved,
	isMessageSelectionAdded,
	isMessageSelectionDelta,
	isMessageSelectionRemoved,
	StreamMessage
} from '../../../api/sportsbook-api-types';
import { BetslipDataItem, AcceptMode } from '../sportsbook-betslip-types';
import { selectAcceptMode } from './betslip-common-duck';
import { calculateAcceptedOdds } from './betslip-duck-utils';
import { PartialBy } from '../../../sportsbook-types';
import { MarketDto, MarketTags, SelectionDto } from '../../../api/sbapi/sbapi-api-model';
import { createSelector } from 'reselect';
import { deepMerge } from '../../../utils/merge-normalized-data-with-stream-messages';

export type BetslipDataItemToAdd = PartialBy<
	// Until we want to allow to control these fiels from somewhere outside this duck
	Omit<BetslipDataItem, 'isLoading'>,
	'acceptedOdds' | 'odds' | 'event' | 'market' | 'selection' | 'hasReplacedData'
>;

export type BetslipDataItemReplacement = { ref: string; market: MarketDto; selection: SelectionDto };

// Constants
const SUBSCRIPTION_REFERENCE = 'betslipDataDuck';

// State
type State = {
	items: Array<BetslipDataItem>;
};

const initialState = { items: [] };

// Selectors
const selectState = (state: RootState) => selectSportsbookState(state).betslip.data;
export const selectItems = (state: RootState) => selectState(state).items;
export const selectItemWithSelectionId = (state: RootState, selectionId: string) =>
	!!selectItems(state).find(item => item.selectionId === selectionId);
export const selectItemCount = (state: RootState) => selectState(state).items.length;
export const selectSelectionIds = createSelector([selectItems], items => items.map(item => item.selectionId));
export const selectIsSelectionInBetslip = (state: RootState, selectionId: string) => {
	const selectionIds = selectSelectionIds(state);
	return selectionIds.some(id => id === selectionId);
};

// Actions
export const ITEMS_ADDED = `${ACTION_PREFIX}BETSLIP/DATA/ITEMS_ADDED` as const;
export type ItemsAddedAction = {
	type: typeof ITEMS_ADDED;
	payload: { items: Array<BetslipDataItemToAdd> };
};

const ITEMS_REMOVED = `${ACTION_PREFIX}BETSLIP/DATA/ITEMS_REMOVED`;
type ItemsRemovedAction = {
	type: typeof ITEMS_REMOVED;
	payload: { selectionIds: Array<string> };
};

const ITEMS_CLEARED = `${ACTION_PREFIX}BETSLIP/DATA/ITEMS_CLEARED`;
type ItemsClearedAction = {
	type: typeof ITEMS_CLEARED;
};

const ITEMS_DATA_REPLACED = `${ACTION_PREFIX}BETSLIP/DATA/ITEMS_DATA_REPLACED`;
type ItemsDataReplacedAction = {
	type: typeof ITEMS_DATA_REPLACED;
	payload: { itemDataReplacements: Array<BetslipDataItemReplacement>; acceptMode: AcceptMode };
};

const DATA_RECEIVED = `${ACTION_PREFIX}BETSLIP/DATA/DATA_RECEIVED` as const;
type DataReceivedAction = {
	type: typeof DATA_RECEIVED;
	payload: {
		data: StreamMessage[];
		acceptMode: AcceptMode;
	};
};

const ITEM_CHANGES_ACCEPTED = `${ACTION_PREFIX}BETSLIP/DATA/ITEM_CHANGES_ACCEPTED` as const;
type ItemChangesAcceptedAction = {
	type: typeof ITEM_CHANGES_ACCEPTED;
};

const DISPOSED = `${ACTION_PREFIX}BETSLIP/DATA/DISPOSED`;

const wasMeaningfulUpdate = (messages: StreamMessage[]): boolean => {
	// eslint-disable-next-line no-restricted-syntax
	for (const m of messages) {
		// Initial state is always meaningful
		if (isMessageInitialState(m)) {
			return true;
		}
		// Removed data are always meaningful
		if (isMessageEventRemoved(m) || isMessageMarketRemoved(m) || isMessageSelectionRemoved(m)) {
			return true;
		}
		// Added data is always meaningful
		if (isMessageEventAdded(m) || isMessageMarketAdded(m) || isMessageSelectionAdded(m)) {
			return true;
		}
		// Event updated suspended state
		if (isMessageEventDelta(m)) {
			const { delta } = m.payload;
			if (delta.isSuspended !== undefined) {
				return true;
			}
		}
		// Market went away from suspended state
		if (isMessageMarketDelta(m)) {
			const { delta } = m.payload;
			if (delta.isSuspended !== undefined) {
				return true;
			}
		}
		// Selection had status changed, or had odds updated
		if (isMessageSelectionDelta(m)) {
			const { delta } = m.payload;
			if (delta.trueOdds !== undefined || delta.status !== undefined) {
				return true;
			}
		}
	}
	return false;
};

// ActionCreators
const initializeEventSubscription =
	(dataUpdatedCallback: () => void): ThunkAction<DataReceivedAction> =>
	(dispatch, getState) => {
		const items = selectItems(getState());
		// Avoid subscribing if there are no current items.
		if (items.length === 0) {
			return;
		}
		const filters = {
			selectionIds: [...new Set(items.map(item => item.selectionId))],
			marketIds: [...new Set(items.map(item => item.marketId))],
			eventIds: [...new Set(items.map(item => item.eventId))]
		};
		subscribeToEvents({
			ref: SUBSCRIPTION_REFERENCE,
			filters,
			onSubscribed: () => {},
			onMessage: data => {
				const acceptMode = selectAcceptMode(getState());
				dispatch({
					type: DATA_RECEIVED,
					payload: { data, acceptMode }
				});

				// Call callback for meaningful updates
				if (wasMeaningfulUpdate(data)) {
					dataUpdatedCallback();
				}
			},
			// eslint-disable-next-line no-console
			onError: console.log
		});
	};

export const unsubscribeFromData = () => {
	unsubscribeFromEvents(SUBSCRIPTION_REFERENCE);
};

export const resubscribeToData =
	(dataUpdatedCallback: () => void): ThunkAction =>
	dispatch => {
		unsubscribeFromData();
		dispatch(initializeEventSubscription(dataUpdatedCallback));
	};

export const addItems = (items: Array<BetslipDataItemToAdd>): ItemsAddedAction => ({
	type: ITEMS_ADDED,
	payload: { items }
});

export const removeItemsBySelectionIds =
	(selectionIds: Array<string>): ThunkAction<ItemsRemovedAction> =>
	dispatch => {
		dispatch({ type: ITEMS_REMOVED, payload: { selectionIds } });
	};

export const clearItems = (): ItemsClearedAction => ({ type: ITEMS_CLEARED });

export const replaceItemsData =
	(itemDataReplacements: BetslipDataItemReplacement[]): ThunkAction<ItemsDataReplacedAction> =>
	(dispatch, getState) => {
		const acceptMode = selectAcceptMode(getState());
		dispatch({
			type: ITEMS_DATA_REPLACED,
			payload: { itemDataReplacements, acceptMode }
		});
	};

export const acceptItemChanges = (): ItemChangesAcceptedAction => ({
	type: ITEM_CHANGES_ACCEPTED
});

export const dispose = (): ThunkAction => dispatch => {
	unsubscribeFromEvents(SUBSCRIPTION_REFERENCE);
	dispatch({ type: DISPOSED });
};

// Reducer
const handleItemsAdded = (state: State, { payload: { items } }: ItemsAddedAction): State => ({
	...state,
	items: [
		...state.items,
		...items.map(item => ({
			...item,
			odds: item.odds ?? 0,
			acceptedOdds: item.acceptedOdds ?? 0,
			isLoading: true,
			hasReplacedData: false
		}))
	]
});

// The idea of this reducer is to allow remove items by selection ids right now,
// but keep on back of head possibility of extending this to accept different predicates (like item ref if we gonna add it).
const handleItemsRemoved = (state: State, { payload: { selectionIds } }: ItemsRemovedAction): State => ({
	...state,
	items: [
		...state.items.filter(
			existingItem => !selectionIds.some(selectionId => selectionId === existingItem.selectionId)
		)
	]
});

const handleItemsCleared = (state: State): State => ({
	...state,
	items: []
});

const handleItemsDataReplaced = (
	state: State,
	{ payload: { itemDataReplacements, acceptMode } }: ItemsDataReplacedAction
): State => ({
	...state,
	items: [
		...state.items.map(item => {
			const itemDataReplacement = itemDataReplacements.find(replacement => replacement.ref === item.ref);
			if (itemDataReplacement) {
				const updatedItem = {
					...item,
					hasReplacedData: true,
					marketId: itemDataReplacement.market.id,
					market: itemDataReplacement.market,
					selectionId: itemDataReplacement.selection.id,
					selection: itemDataReplacement.selection
				};
				updatedItem.acceptedOdds = calculateAcceptedOdds(
					updatedItem.odds,
					updatedItem.acceptedOdds,
					acceptMode
				);
				return updatedItem;
			}
			return item;
		})
	]
});

const handleDataReceived = (state: State, { payload }: DataReceivedAction): State => {
	// Update all items
	let nextItems = payload.data.reduce(
		(items, m) => {
			if (isMessageInitialState(m)) {
				return items.map(item => {
					const selection = m.payload.selections.find(e => e.id === item.selectionId) || item.selection;
					const nextOdds = selection?.trueOdds || 0;
					const nextItem = {
						...item,
						event: m.payload.events.find(e => e.id === item.eventId) || item.event,
						market: m.payload.markets.find(e => e.id === item.marketId) || item.market,
						selection,
						odds: nextOdds,
						acceptedOdds: item.isLoading ? nextOdds : item.acceptedOdds,
						isLoading: false
					};

					if (item.isLoading && nextItem.market?.tags.includes(MarketTags.IS_PREFERRED)) {
						nextItem.preferredInMarketType = nextItem.market.marketType.id;
					}

					return nextItem;
				});
			}
			if (isMessageEventAdded(m)) {
				const { event } = m.payload;
				return items.map(item => (item.eventId === event.id ? { ...item, event } : item));
			}
			if (isMessageEventDelta(m)) {
				const { delta } = m.payload;
				return items.map(item =>
					item.eventId === delta.id ? { ...item, event: deepMerge(item.event, delta) } : item
				);
			}
			if (isMessageMarketAdded(m)) {
				const { market } = m.payload;
				return items.map(item => (item.marketId === market.id ? { ...item, market } : item));
			}
			if (isMessageMarketDelta(m)) {
				const { delta } = m.payload;
				return items.map(item =>
					item.marketId === delta.id ? { ...item, market: deepMerge(item.market, delta) } : item
				);
			}
			if (isMessageSelectionAdded(m)) {
				const { selection } = m.payload;
				return items.map(item =>
					item.selectionId === selection.id ? { ...item, selection, odds: selection.trueOdds } : item
				);
			}
			if (isMessageSelectionDelta(m)) {
				const { delta } = m.payload;
				return items.map(item =>
					item.selectionId === delta.id
						? {
								...item,
								selection: deepMerge(item.selection, delta),
								odds: delta.trueOdds || item.odds
							}
						: item
				);
			}
			return items;
		},
		[...state.items] as Array<BetslipDataItem>
	);

	// recalculate accepted odds
	nextItems = nextItems.map(item => ({
		...item,
		acceptedOdds: calculateAcceptedOdds(item.odds, item.acceptedOdds, payload.acceptMode)
	}));

	// Return next state
	return { ...state, items: nextItems };
};

const handleChangesAccepted = (state: State) => {
	return {
		...state,
		items: state.items.map(item => ({ ...item, acceptedOdds: item.odds, hasReplacedData: false }))
	};
};

const handleDisposed = () => initialState;

const reducer = createReducer<State>(initialState, {
	[ITEMS_ADDED]: handleItemsAdded,
	[ITEMS_REMOVED]: handleItemsRemoved,
	[ITEMS_CLEARED]: handleItemsCleared,
	[ITEMS_DATA_REPLACED]: handleItemsDataReplaced,
	[DATA_RECEIVED]: handleDataReceived,
	[ITEM_CHANGES_ACCEPTED]: handleChangesAccepted,
	[DISPOSED]: handleDisposed
});

export default reducer;
